import xlsx from 'node-xlsx';

export const MAX_FILE_SIZE = 10 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = new Set(['csv', 'xlsx', 'xls']);

export const FILE_ERRORS = {
  FILE_SIZE_EXCEEDED: 'File size exceeds maximum limit of 10MB',
  UNSUPPORTED_FORMAT: 'Unsupported file format. Please use CSV, XLSX, or XLS files.',
  NO_WORKSHEETS: 'No worksheets found in the file',
  INSUFFICIENT_DATA: 'File must contain a header row and at least one data row',
  PARSE_FAILED: 'Failed to parse file. Please ensure it is a valid spreadsheet file.',
} as const;

export interface ParsedPartNumberFile {
  filename: string;
  headers: string[];
  rows: string[][];
  suggestedColumnIndex: number;
}

const PART_NUMBER_HEADER = /\b(part[\s_-]*(no|num|number)?|nsn|niin|stock[\s_-]*number)\b/i;

const detectColumn = (headers: string[]): number => {
  const index = headers.findIndex(header => PART_NUMBER_HEADER.test(header?.trim() ?? ''));
  return index === -1 ? 0 : index;
};

type SpreadsheetCell = string | number | boolean | null | undefined;

const isBlankRow = (row: readonly string[] | undefined): boolean => !row || row.every(cell => cell.trim() === '');

export const parsePartNumberFile = async (file: File): Promise<ParsedPartNumberFile> => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(FILE_ERRORS.FILE_SIZE_EXCEEDED);
  }

  const extension = file.name.toLowerCase().split('.').pop();
  if (!extension || !SUPPORTED_EXTENSIONS.has(extension)) {
    throw new Error(FILE_ERRORS.UNSUPPORTED_FORMAT);
  }

  try {
    const arrayBuffer = await file.arrayBuffer();
    const worksheets = xlsx.parse(arrayBuffer);

    if (!worksheets || worksheets.length === 0) {
      throw new Error(FILE_ERRORS.NO_WORKSHEETS);
    }

    const sheets = worksheets.map(worksheet => (worksheet.data as SpreadsheetCell[][]) ?? []);
    const sheet = sheets.find(data => data.length >= 2) ?? sheets[0];

    const headers = (sheet[0] ?? []).map(header => String(header ?? '').trim());
    const rows = sheet.slice(1).map(row => (row ?? []).map(cell => String(cell ?? '')));

    while (rows.length > 0 && isBlankRow(rows.at(-1))) {
      rows.pop();
    }

    if (rows.length === 0) {
      throw new Error(FILE_ERRORS.INSUFFICIENT_DATA);
    }

    return {
      filename: file.name,
      headers,
      rows,
      suggestedColumnIndex: detectColumn(headers),
    };
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(FILE_ERRORS.PARSE_FAILED);
  }
};
